export const NETWORK_FOLLOW_STATES = {
  AUTO: "AUTO_FOLLOW",
  MANUAL_UNFOLLOW: "MANUAL_UNFOLLOW",
  MANUAL_FOLLOW: "MANUAL_FOLLOW",
};

const STORAGE_KEY = "kewordal:network-follow-states:v1";

const normalizeFollowUserId = (value) => {
  if (value == null) return null;

  const normalized = String(value).trim();
  if (!normalized) return null;

  const lowered = normalized.toLowerCase();
  if (lowered === "undefined" || lowered === "null") return null;

  return normalized;
};

const getFollowPairKey = (followerId, followeeId) => {
  const followerKey = normalizeFollowUserId(followerId);
  const followeeKey = normalizeFollowUserId(followeeId);

  return followerKey && followeeKey ? `${followerKey}->${followeeKey}` : null;
};

const canUseLocalStorage = () =>
  typeof window !== "undefined" && Boolean(window.localStorage);

const readFollowStateStore = () => {
  if (!canUseLocalStorage()) return {};

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};

    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const writeFollowStateStore = (store) => {
  if (!canUseLocalStorage()) return;

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    /* localStorage can fail in private mode; the server state still applies. */
  }
};

export const getNetworkFollowState = (followerId, followeeId) => {
  const key = getFollowPairKey(followerId, followeeId);
  if (!key) return null;

  const entry = readFollowStateStore()[key];
  return Object.values(NETWORK_FOLLOW_STATES).includes(entry?.state)
    ? entry.state
    : null;
};

export const setNetworkFollowState = (followerId, followeeId, state) => {
  const key = getFollowPairKey(followerId, followeeId);
  if (!key || !Object.values(NETWORK_FOLLOW_STATES).includes(state)) {
    return null;
  }

  const store = readFollowStateStore();
  store[key] = {
    state,
    updatedAt: new Date().toISOString(),
  };
  writeFollowStateStore(store);

  return state;
};

export const clearNetworkFollowState = (followerId, followeeId) => {
  const key = getFollowPairKey(followerId, followeeId);
  if (!key) return;

  const store = readFollowStateStore();
  if (!store[key]) return;

  delete store[key];
  writeFollowStateStore(store);
};

export const clearAutoNetworkFollowState = (followerId, followeeId) => {
  if (getNetworkFollowState(followerId, followeeId) === NETWORK_FOLLOW_STATES.AUTO) {
    clearNetworkFollowState(followerId, followeeId);
  }
};

const readBooleanField = (record, keys) =>
  keys
    .map((key) => record?.[key])
    .find((value) => typeof value === "boolean");

const normalizeStateText = (value) => String(value || "").trim().toUpperCase();

export const inferNetworkFollowState = (record) => {
  if (!record || typeof record !== "object") return null;

  if (
    readBooleanField(record, [
      "manualUnfollow",
      "isManualUnfollow",
      "manualUnfollowOverride",
    ]) === true
  ) {
    return NETWORK_FOLLOW_STATES.MANUAL_UNFOLLOW;
  }

  if (
    readBooleanField(record, [
      "manualFollow",
      "isManualFollow",
      "userCreated",
      "isUserCreated",
    ]) === true
  ) {
    return NETWORK_FOLLOW_STATES.MANUAL_FOLLOW;
  }

  if (
    readBooleanField(record, [
      "autoFollow",
      "isAutoFollow",
      "automatic",
      "isAutomatic",
      "systemCreated",
      "isSystemCreated",
    ]) === true
  ) {
    return NETWORK_FOLLOW_STATES.AUTO;
  }

  const stateText = [
    record?.networkFollowState,
    record?.followState,
    record?.followStatus,
    record?.relationshipState,
    record?.relationshipStatus,
    record?.followType,
    record?.relationshipType,
    record?.source,
    record?.followSource,
    record?.origin,
    record?.createdByType,
    record?.createdBy,
  ]
    .map(normalizeStateText)
    .filter(Boolean)
    .join(" ");

  if (stateText.includes("MANUAL_UNFOLLOW")) {
    return NETWORK_FOLLOW_STATES.MANUAL_UNFOLLOW;
  }

  if (
    stateText.includes("MANUAL") ||
    stateText.includes("USER_CREATED") ||
    stateText.includes("USER_INITIATED") ||
    stateText.includes("USER_OWNED")
  ) {
    return NETWORK_FOLLOW_STATES.MANUAL_FOLLOW;
  }

  if (
    stateText.includes("AUTO") ||
    stateText.includes("AUTOMATIC") ||
    stateText.includes("CONNECTION_REQUEST") ||
    stateText.includes("REQUEST_AUTO") ||
    stateText.includes("SYSTEM")
  ) {
    return NETWORK_FOLLOW_STATES.AUTO;
  }

  return null;
};

export const getEffectiveNetworkFollowing = ({
  followerId,
  followeeId,
  isFollowing = false,
  serverFollowState = null,
  hasConnectionContext = false,
}) => {
  const storedState = getNetworkFollowState(followerId, followeeId);

  if (storedState === NETWORK_FOLLOW_STATES.MANUAL_UNFOLLOW) return false;
  if (storedState === NETWORK_FOLLOW_STATES.MANUAL_FOLLOW) return true;
  if (storedState === NETWORK_FOLLOW_STATES.AUTO && hasConnectionContext) {
    return true;
  }

  if (serverFollowState === NETWORK_FOLLOW_STATES.MANUAL_UNFOLLOW) return false;
  if (
    serverFollowState === NETWORK_FOLLOW_STATES.MANUAL_FOLLOW ||
    serverFollowState === NETWORK_FOLLOW_STATES.AUTO
  ) {
    return true;
  }

  return Boolean(isFollowing);
};

export const shouldCreateAutoNetworkFollow = (followerId, followeeId) => {
  const storedState = getNetworkFollowState(followerId, followeeId);
  return (
    storedState !== NETWORK_FOLLOW_STATES.MANUAL_FOLLOW &&
    storedState !== NETWORK_FOLLOW_STATES.MANUAL_UNFOLLOW
  );
};

export const shouldRemoveAutoNetworkFollow = (
  followerId,
  followeeId,
  followRecord = null,
) => {
  const storedState = getNetworkFollowState(followerId, followeeId);

  if (storedState === NETWORK_FOLLOW_STATES.AUTO) return true;
  if (
    storedState === NETWORK_FOLLOW_STATES.MANUAL_FOLLOW ||
    storedState === NETWORK_FOLLOW_STATES.MANUAL_UNFOLLOW
  ) {
    return false;
  }

  return inferNetworkFollowState(followRecord) === NETWORK_FOLLOW_STATES.AUTO;
};
